import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

import ModalPortal from '../../../../components/modal-portal';
import { Modal } from 'ui-components/components/modal';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalFooterCancelButton } from 'ui-components/components/modal/modal-footer/cancel-button';
import { Button } from 'ui-components/components/button';
import {
  NotificationService,
  NOTIFICATION_TYPE_ERROR,
  NOTIFICATION_TYPE_SUCCESS,
  NOTIFICATION_TYPE_INFO,
} from 'ui-components/services/notification-service';

import { deactivateMarketplace as deactivateMarketplaceAsync } from '../../api/fulfillment-delegation';
import { refreshStoreList } from '../../../../store/seller/seller.actions';

import messages from './messages';
import style from './style.module.less';

export const DeactivateMarketplaceModal = ({ onClose, storeId, storeName = 'Marketplace' }) => {
  const dispatch = useDispatch();

  const updateNotification = (notificationId, notificationType, title, content) => {
    NotificationService.updateNotification(notificationId, {
      title,
      content,
      notificationType,
      progress: false,
      dontHide: false,
    });
  };

  const deactivateMarketplace = useCallback(async () => {
    onClose();
    const notificationId = NotificationService.showNotification({
      title: <FormattedMessage {...messages.deactivatingMarketplaceTitle} />,
      content: <FormattedMessage {...messages.deactivatingMarketplaceMessage} values={{ storeName }} />,
      notificationType: NOTIFICATION_TYPE_INFO,
      progress: true,
      dontHide: true,
    });
    try {
      await deactivateMarketplaceAsync(storeId);
      updateNotification(
        notificationId,
        NOTIFICATION_TYPE_SUCCESS,
        <FormattedMessage {...messages.succesfulDeactivationTitle} />,
        <FormattedMessage {...messages.succesfulDeactivationMessage} values={{ storeName }} />,
      );
      dispatch(refreshStoreList());
    } catch {
      updateNotification(
        notificationId,
        NOTIFICATION_TYPE_ERROR,
        <FormattedMessage {...messages.failedDeactivationTitle} />,
        <FormattedMessage {...messages.failedDeactivationMessage} />,
      );
    }
  }, [dispatch, onClose, storeId, storeName]);

  return (
    <ModalPortal onClose={onClose}>
      <Modal size='medium'>
        <ModalHeader>
          <FormattedMessage {...messages.modalHeader} values={{ storeName }} />
        </ModalHeader>
        <ModalBody>
          <div className={style['deactivate-marketplace-modal-body']}>
            <ul>
              <li>
                <FormattedMessage {...messages.deactivateMarketplaceBody} />
              </li>
              <li>
                <FormattedMessage {...messages.deactivateMarketplaceBodyStep1} values={{ storeName }} />
              </li>
              <li>
                <FormattedMessage {...messages.deactivateMarketplaceBodyStep2} values={{ storeName }} />
              </li>
            </ul>
          </div>
        </ModalBody>
        <ModalFooter>
          <ModalFooterCancelButton />
          <Button onClick={deactivateMarketplace} theme='danger'>
            <FormattedMessage {...messages.deactivateMarketplaceButton} />
          </Button>
        </ModalFooter>
      </Modal>
    </ModalPortal>
  );
};

DeactivateMarketplaceModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  storeId: PropTypes.number.isRequired,
  storeName: PropTypes.string,
};
